"use client";

import type {
  StepTransitionPhase,
  StepTransitionSettings,
  StepTransitionWrapperProps,
} from "./form-types";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrefersReducedMotion } from "~www/hooks/use-prefers-reduced-motion";
import { resolveStepTransitionConfig } from "./step-transition-constants";
import {
  isStepTransitionExiting,
  runStepTransitionSequence,
  STEP_TRANSITION_PHASE,
} from "./step-transition-sequence";

type AdvanceOptions = {
  /** Skips the configured delay before the exit starts. Used by Back, which never waits. */
  skipDelay?: boolean;
};

type UseStepTransitionReturn = {
  /** Spread onto the step grid. Static values when transitions are off. */
  wrapperProps: StepTransitionWrapperProps;
  /**
   * Runs `advance` at the point in the sequence where the step content should
   * swap. Calls it synchronously when transitions are off or the user prefers
   * reduced motion, so advance timing is unchanged from an unanimated form.
   */
  advanceWithTransition: (
    advance: () => void,
    options?: AdvanceOptions,
  ) => void;
  /**
   * Cancels an in-flight `advanceWithTransition` sequence without ever
   * calling the deferred `advance`. For callers like the takeover's close
   * button, which must win immediately rather than be blocked by
   * `isTransitionInFlight`.
   */
  cancelInFlightTransition: () => void;
  /**
   * Reads whether an `advanceWithTransition` sequence is currently in
   * flight. A function (not a boolean) so its identity stays stable across
   * renders — consumers like Back's guard can depend on it without busting
   * memoization on every phase change.
   */
  isTransitionInFlight: () => boolean;
};

function useStepTransition(
  settings: StepTransitionSettings | null | undefined,
): UseStepTransitionReturn {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [phase, setPhase] = useState<StepTransitionPhase>(
    STEP_TRANSITION_PHASE.idle,
  );
  // A ref, not state: the guard must not change `advanceWithTransition`'s
  // identity mid-sequence, or memoized step items re-render on every phase.
  const isAdvancingRef = useRef(false);
  const timerIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const isAnimated = Boolean(settings?.enabled) && !prefersReducedMotion;

  const { delayBeforeExitMs, enterDurationMs, exitDurationMs, variant } =
    useMemo(() => resolveStepTransitionConfig(settings), [settings]);

  useEffect(() => {
    return () => {
      for (const timerId of timerIdsRef.current) {
        clearTimeout(timerId);
      }
      timerIdsRef.current = [];
    };
  }, []);

  const advanceWithTransition = useCallback(
    (advance: () => void, options?: AdvanceOptions) => {
      if (!isAnimated) {
        advance();
        return;
      }
      // Blocks a second advance while one is in flight — an auto-advance radio
      // and a Continue click can otherwise both send NEXT and skip a step.
      if (isAdvancingRef.current) {
        return;
      }
      isAdvancingRef.current = true;

      // Safe to drop the previous ids: the guard above guarantees every timer
      // from the last sequence has already fired.
      timerIdsRef.current = [];

      runStepTransitionSequence(
        {
          delayBeforeExitMs,
          enterDurationMs,
          exitDurationMs,
        },
        options,
        {
          advance,
          onPhaseChange: (nextPhase) => {
            setPhase(nextPhase);
            if (nextPhase === STEP_TRANSITION_PHASE.idle) {
              isAdvancingRef.current = false;
            }
          },
          schedule: (callback, delayMs) => {
            timerIdsRef.current.push(setTimeout(callback, delayMs));
          },
        },
      );
    },
    [delayBeforeExitMs, enterDurationMs, exitDurationMs, isAnimated],
  );

  // Memoized like its siblings — useLeadFormTakeover's onCloseTakeover depends on it.
  const cancelInFlightTransition = useCallback(() => {
    for (const timerId of timerIdsRef.current) {
      clearTimeout(timerId);
    }
    timerIdsRef.current = [];
    isAdvancingRef.current = false;
    setPhase(STEP_TRANSITION_PHASE.idle);
  }, []);

  const isExiting = isStepTransitionExiting(phase);

  const wrapperProps = useMemo<StepTransitionWrapperProps>(
    () => ({
      enterDurationMs,
      exitDurationMs,
      isAnimated,
      isExiting,
      variant,
    }),
    [enterDurationMs, exitDurationMs, isAnimated, isExiting, variant],
  );

  const isTransitionInFlight = useCallback(() => isAdvancingRef.current, []);

  return {
    advanceWithTransition,
    cancelInFlightTransition,
    isTransitionInFlight,
    wrapperProps,
  };
}

export { useStepTransition };
